import {
  type FC,
  type KeyboardEvent,
  type ReactNode,
  useEffect,
  useRef,
  useState,
} from "react";

interface ScrollSafeDropdownProps {
  id: string;
  describedBy?: string;
  invalid?: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  trigger: ReactNode;
  children: ReactNode;
  menuClassName?: string;
}

export const ScrollSafeDropdown: FC<ScrollSafeDropdownProps> = ({
  id,
  describedBy,
  invalid = false,
  open,
  onOpenChange,
  trigger,
  children,
  menuClassName = "",
}) => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;

    const handlePointerDown = (event: PointerEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        onOpenChange(false);
      }
    };

    const handleKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key === "Escape") {
        onOpenChange(false);
      }
    };

    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open, onOpenChange]);

  const handleContainerKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Escape" && open) {
      event.preventDefault();
      onOpenChange(false);
    }
  };

  return (
    <div
      ref={containerRef}
      className="position-relative blanket-scroll-safe-dropdown"
      onKeyDown={handleContainerKeyDown}
    >
      <div
        id={id}
        aria-describedby={describedBy}
        aria-expanded={open}
        aria-haspopup="listbox"
        className={invalid ? "is-invalid" : undefined}
      >
        {trigger}
      </div>
      {open ? (
        <div
          role="listbox"
          aria-labelledby={id}
          className={`blanket-dropdown-menu ${menuClassName}`.trim()}
        >
          {children}
        </div>
      ) : null}
    </div>
  );
};

export const useScrollSafeDropdown = () => {
  const [open, setOpen] = useState(false);
  return { open, setOpen, toggle: () => setOpen((current) => !current) };
};
